// An answer option with our own marks (heart, plus, capsule, bolt; check and cross for true or false).
// variant 'row' = full width (mark badge, text); 'tile' = grid tile (2 × 2 and true or false); 'mark' = classroom mode,
// the mark only, huge (the text stays available to screen readers).
// States: idle · locked (M5: squash then spring, a glow ring in its colour, a ripple from the tap point) · dim (the
// others shrink and fade) · correct (solid emerald, a tick, pulses) · wrong (rose, a cross, defib shake). `share`
// (0–1) shows the room's split on reveal. Every effect is transform or opacity; reduced motion keeps only the states.
import { useLayoutEffect, useEffect, useRef, type CSSProperties, type PointerEvent } from 'react';
import { Check, X } from 'lucide-react';
import type { Format } from '@shared/types';
import { AnswerMark, MarkBadge, markByKind, markForOption, type GlyphKind } from '@/brand/marks';
import { canAnimate, prefersReducedMotion } from '@/motion/reduced';

export type OptionState = 'idle' | 'selected' | 'locked' | 'correct' | 'wrong' | 'dim';
/** variant: 'row' = full-width mark + letter + text; 'tile' = grid tile; 'mark' = classroom mode, answer mark and letter only. */
export type OptionVariant = 'row' | 'tile' | 'mark';

const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const isLock = (s: OptionState) => s === 'locked' || s === 'selected';

export interface OptionButtonProps {
  id: string; text: string;
  /** Position in the question (0-based): picks the mark (A heart, B plus, C capsule, D bolt). */
  index: number;
  state: OptionState;
  disabled?: boolean;
  onSelect?: (id: string) => void;
  variant?: OptionVariant;
  /** 'tf' draws the true (check, cobalt) and false (cross, orange) marks. */
  format?: Format;
  /** Override the mark. */
  mark?: GlyphKind;
  /** The room's share for this option, 0–1, shown on reveal. */
  share?: number;
  /** Delay before the correct or wrong effect plays, to choreograph the reveal. */
  revealDelayMs?: number;
  /** A row kept short, with a smaller mark (the clue question's answers, docked under the cards). */
  dense?: boolean;
  className?: string;
}

export function OptionButton({ id, text, index, state, disabled, onSelect, variant = 'row', format, mark, share, revealDelayMs = 0, dense = false, className = '' }: OptionButtonProps) {
  const spec = mark ? markByKind(mark) : markForOption(format, index);
  const tf = spec.kind === 'check' || spec.kind === 'cross';
  const ref = useRef<HTMLButtonElement>(null);
  const ringRef = useRef<HTMLSpanElement>(null);
  const haloRef = useRef<HTMLSpanElement>(null);
  const clipRef = useRef<HTMLSpanElement>(null);
  const tap = useRef<{ x: number; y: number } | undefined>(undefined);
  // A tile that mounts already revealed still plays its effect: the reveal screen usually mounts fresh.
  const prev = useRef<OptionState>(state === 'correct' || state === 'wrong' ? 'locked' : state);

  useIso(() => {
    const el = ref.current; const from = prev.current; prev.current = state;
    if (!el || from === state || !canAnimate() || prefersReducedMotion()) return;
    const running: Animation[] = [];
    if (isLock(state) && !isLock(from)) {
      running.push(el.animate([
        { transform: 'translate3d(0,0,0) scale(1)' }, { transform: 'translate3d(0,2px,0) scale(0.94)', offset: 0.22 },
        { transform: 'translate3d(0,1px,0) scale(1.03)', offset: 0.6 }, { transform: 'translate3d(0,2px,0) scale(1)' },
      ], { duration: 380, easing: 'ease-out' }));
      if (ringRef.current) running.push(ringRef.current.animate([{ opacity: 0.8, transform: 'scale(0.97)' }, { opacity: 0, transform: 'scale(1.12)' }], { duration: 560, easing: 'cubic-bezier(.2,.8,.2,1)' }));
      if (clipRef.current) running.push(ripple(el, clipRef.current, tap.current));
    }
    if (state === 'correct') {
      running.push(el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.04)', offset: 0.35 }, { transform: 'scale(1)' }], { duration: 440, delay: revealDelayMs, easing: 'ease-out' }));
      if (haloRef.current) running.push(haloRef.current.animate([{ opacity: 0, transform: 'scale(0.98)' }, { opacity: 0.9, transform: 'scale(1.02)', offset: 0.3 }, { opacity: 0, transform: 'scale(1.1)' }], { duration: 900, delay: revealDelayMs + 120, iterations: 2, easing: 'ease-out' }));
    }
    if (state === 'wrong') {
      running.push(el.animate([
        { transform: 'translate3d(0,0,0) rotate(0)' }, { transform: 'translate3d(-11px,0,0) rotate(-1.2deg)', offset: 0.1 },
        { transform: 'translate3d(9px,0,0) rotate(1deg)', offset: 0.26 }, { transform: 'translate3d(-6px,0,0) rotate(-0.6deg)', offset: 0.44 },
        { transform: 'translate3d(4px,0,0)', offset: 0.62 }, { transform: 'translate3d(-2px,0,0)', offset: 0.8 }, { transform: 'translate3d(0,0,0)' },
      ], { duration: 480, delay: revealDelayMs, easing: 'ease-out' }));
    }
    return () => { /* effects are one-shot; let them finish */ void running; };
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    tap.current = { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const vars = { '--c': spec.colour, '--soft': spec.soft, '--deep': spec.deep, '--on': spec.ink } as CSSProperties;
  const pct = share === undefined ? undefined : Math.round(Math.min(1, Math.max(0, share)) * 100);
  const correct = state === 'correct'; const wrong = state === 'wrong';
  const badgeSpec = correct ? { ...spec, colour: '#FFFFFF', ink: 'var(--correct-strong)' } : spec;
  const status = correct
    ? <span className="rd-opt-status bg-surface text-correct-strong" role="img" aria-label="Correct answer"><Check className="size-4" strokeWidth={3.4} aria-hidden="true" /></span>
    : wrong ? <span className="rd-opt-status bg-wrong text-white" role="img" aria-label="Your answer, wrong"><X className="size-4" strokeWidth={3.4} aria-hidden="true" /></span>
    : null;

  return (
    <button ref={ref} type="button" disabled={disabled} onClick={() => onSelect?.(id)} onPointerDown={onPointerDown}
      aria-pressed={isLock(state)} data-state={state} data-variant={variant} data-tf={tf || undefined} data-mark={spec.kind} data-dense={dense && variant === 'row' && !tf ? '' : undefined}
      className={className ? `rd-opt ${className}` : 'rd-opt'} style={vars}>
      <span ref={clipRef} className="rd-opt-clip" aria-hidden="true">
        {pct !== undefined && <span className="rd-opt-share" style={{ ['--share' as string]: String(pct / 100) } as CSSProperties} />}
      </span>
      <span ref={ringRef} className="rd-opt-ring" aria-hidden="true" />
      <span ref={haloRef} className="rd-opt-halo" aria-hidden="true" />

      {variant === 'mark' ? (
        <>
          <span aria-hidden="true" className="numeral absolute left-3 top-2.5 text-2xl leading-none" style={{ color: spec.ink }}>{tf ? '' : spec.letter}</span>
          <span aria-hidden="true" className="inline-flex"><AnswerMark kind={spec.kind} size={76} /></span>
          <span className="sr-only">{spec.letter}. {text}</span>
          {status}
        </>
      ) : variant === 'tile' && tf ? (
        <>
          <span className="relative inline-flex"><MarkBadge mark={badgeSpec} size={56} letter={false} />{status}</span>
          <span className="min-w-0">{text}</span>
          {pct !== undefined && <span className="rd-opt-pct">{pct}%</span>}
        </>
      ) : variant === 'tile' ? (
        <>
          <span className="relative inline-flex"><MarkBadge mark={badgeSpec} size={46} />{status}</span>
          <span className="min-w-0 wrap-anywhere">{text}</span>
          {pct !== undefined && <span className="rd-opt-pct">{pct}%</span>}
        </>
      ) : (
        <>
          <span className="relative inline-flex"><MarkBadge mark={badgeSpec} size={dense && !tf ? 32 : 46} letter={!tf} />{status}</span>
          <span className="min-w-0 flex-1 wrap-anywhere">{text}</span>
          {pct !== undefined && <span className="rd-opt-pct">{pct}%</span>}
        </>
      )}
    </button>
  );
}

/** A ripple from the tap point (or the centre for keyboard presses), clipped to the option. */
function ripple(el: HTMLElement, clip: HTMLElement, at?: { x: number; y: number }): Animation {
  const w = el.clientWidth; const h = el.clientHeight;
  const x = at?.x ?? w / 2; const y = at?.y ?? h / 2;
  const d = 2.3 * Math.max(Math.hypot(x, y), Math.hypot(w - x, y), Math.hypot(x, h - y), Math.hypot(w - x, h - y));
  const dot = document.createElement('span');
  dot.className = 'rd-opt-ripple';
  dot.style.cssText = `left:${x}px;top:${y}px;width:${d}px;height:${d}px`;
  clip.appendChild(dot);
  const a = dot.animate([{ opacity: 0.4, transform: 'translate3d(-50%,-50%,0) scale(0)' }, { opacity: 0, transform: 'translate3d(-50%,-50%,0) scale(0.5)' }], { duration: 620, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
  a.onfinish = () => dot.remove(); a.oncancel = () => dot.remove();
  return a;
}
